import { useChartSeries } from '../ChartSeriesContext'

import { buildNetChangeRows, signedNumber } from './buildNetChangeRows'

import type { JSX } from 'react'

/** Signed percent, or an em-dash when there's no baseline (started at zero). */
function formatPct(pct: number | null): string {
  if (pct == null) return '—'
  const sign = pct >= 0 ? '+' : '−'
  return `${sign}${Math.abs(pct).toFixed(1)}%`
}

interface NetChangeDataTableProps {
  /** Names the table (the chart's accessible label). */
  caption: string
}

/**
 * A screen-reader data alternative for the diverging net-change chart, rendered
 * inside {@link ChartFrame}'s visually-hidden disclosure. Reads the series
 * registry from context (same source as the bars) so it can't drift: one row per
 * channel with its net change, start, current value, and percent change as text —
 * the values the SVG bars can't announce on their own.
 */
export function NetChangeDataTable({
  caption,
}: NetChangeDataTableProps): JSX.Element | null {
  const series = useChartSeries()
  const rows = buildNetChangeRows(series)

  if (rows.length === 0) return null

  const labelById = new Map(series.map((s) => [s.id, s.label]))
  const summary = rows
    .map((r) => `${labelById.get(r.id) ?? r.id}: ${signedNumber(r.delta)}`)
    .join('. ')

  return (
    <table>
      <caption>{`${caption}. ${summary}`}</caption>
      <thead>
        <tr>
          <th scope="col">Channel</th>
          <th scope="col">Net change</th>
          <th scope="col">At start</th>
          <th scope="col">Now</th>
          <th scope="col">% change</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <th scope="row">{labelById.get(r.id) ?? r.id}</th>
            <td>{signedNumber(r.delta)}</td>
            <td>{r.start.toLocaleString()}</td>
            <td>{r.end.toLocaleString()}</td>
            <td>{formatPct(r.pct)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
